import { LoadingArea } from '@consigli/facade';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureFlag, useFeatureFlag, useFeatureFlagsReady } from '@/feature-flags';
import { ProjectCreation } from '@/features/project-creation/project-creation';

import { CreateProjectPage } from './create-project-page';

/**
 * Chooses between the current project creation form and the redesign.
 *
 * Waits for `useFeatureFlagsReady` rather than reading the flag alone: an unread
 * flag resolves to `false`, so rendering immediately would paint the old form
 * and then swap it once the first fetch lands — and here that would also move
 * the fields the user had already started filling in. Modelled on
 * project-list-route.tsx, which exists for the same reason.
 *
 * When flags are not configured — local development without Unleash — `ready`
 * is always `true` and this resolves to the current form on the first render.
 */
export const ProjectCreationRoute: FC = () => {
  const { t } = useTranslation();
  const ready = useFeatureFlagsReady();
  const redesigned = useFeatureFlag(FeatureFlag.AppRedesign);

  if (!ready) {
    return (
      <div className="flex items-center justify-center">
        <LoadingArea title={t('project-list.loading-title')} className="mx-auto mt-[25vh]" />
      </div>
    );
  }

  return redesigned ? <CreateProjectPage /> : <ProjectCreation />;
};
